import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FlyInPanel, MessageBar, ProgressRing, SearchBox, Text } from '../../providers/harmony-scope/lazy-enablers';
import { useMEMTranslation } from '../../utils/text-utils';
import { useTrackEvent } from '../../services/logging/hooks';
import { usePageContext } from '../../utils/page-context';
import { useGetDataRefreshDetailsQuery } from '../../ducks/api-ducks/data-refresh-details-duck/api-duck-data-refresh-details';
import { useAppSelector } from '../../ducks/hooks';
import DataRefreshSourceRow from './data-refresh-source-row';
import { largePanelStyle } from '../../utils/common-styles';
import { dataRefreshPanelGridTemplateColumns } from './layout-constants';
import { searchBoxStyle } from '../../utils/common-styles';
import { TDataRefreshDetail } from '../../ducks/api-ducks/data-refresh-details-duck/types-data-refresh-details';

interface IProps {
  closePanel: () => void;
}

const componentName = 'Data Refresh Panel';
const PRODUCT_FINDER_SOURCE_NAME = 'Product Finder';

const headerRowStyles: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: dataRefreshPanelGridTemplateColumns,
  gap: '0.75rem',
  padding: '0.75rem 0',
  borderBottom: '1px solid var(--he-color-neutral-300, #d1d1d1)',
  position: 'sticky',
  top: 0,
  zIndex: 1,
  background: 'var(--he-color-neutral-background-1, #ffffff)',
};

const loadingContainerStyles: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  padding: '2rem',
};

const contentContainerStyles: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',
  maxHeight: 'calc(100vh - 300px)',
};

const searchContainerStyles: React.CSSProperties = {
  display: 'flex',
  marginTop: '0.75rem',
  marginBottom: '0.5rem',
};

const infoContainerStyles: React.CSSProperties = {
  background: 'var(--he-color-neutral-background-1, #ffffff)',
  padding: 0,
  marginBottom: '0.5rem',
};

export const DataRefreshPanel = (props: IProps) => {
  const { t } = useMEMTranslation();
  const pageName = usePageContext();
  const trackEvent = useTrackEvent();
  const { closePanel: parentClosePanel } = props;
  const [searchText, setSearchText] = useState<string>('');

  /* isPanelOpen mirrors the notifications panel pattern: the parent owns visibility, this state
   * just drives the open/close css transition so the panel does not flicker open. */
  const [isPanelOpen, setPanelOpen] = useState<boolean>(false);
  useEffect(() => {
    setPanelOpen(true);
  }, []);

  const impersonation = useAppSelector((state) => state.impersonation);
  const participationDetails = useAppSelector((state) => state.participationDetails);
  const filters = useAppSelector((state) => state.filters);

  // Get the fiscal year from their selected participation to ensure correct data sync
  const selectedParticipation = useMemo(() => {
    if (!(participationDetails && filters?.participationId && filters.selectedParticipationPartitionGroup)) {
      return undefined;
    }

    return participationDetails.coreParticipations.find(
      (participation) =>
        filters.participationId === participation.participationId &&
        filters.selectedParticipationPartitionGroup?.partitionGroup === participation.fiscalYearId.toString()
    );
  }, [participationDetails, filters.participationId, filters.selectedParticipationPartitionGroup]);

  const {
    data: sourceRefreshDetails,
    isFetching,
    isError,
  } = useGetDataRefreshDetailsQuery(
    selectedParticipation
      ? {
          cacheKey: impersonation.impersonatedUser,
          customParams: {
            fiscalYearId: String(selectedParticipation.fiscalYearId),
            participationId: String(selectedParticipation.participationId),
            personnelNumber: String(participationDetails?.personnelNumber),
          },
          noFilters: true,
        }
      : { cacheKey: impersonation.impersonatedUser }
  );

  // delay parent close to allow the css transition to play (see notifications panel for rationale)
  const closePanel = useCallback(() => {
    setTimeout(parentClosePanel, 0);
  }, [parentClosePanel]);

  const handlePanelClosing = useCallback(() => {
    trackEvent('Data refresh panel closed', pageName, componentName);
    closePanel();
  }, [closePanel, pageName, trackEvent]);

  useEffect(() => {
    if (isPanelOpen) {
      trackEvent('Data refresh panel opened', pageName, componentName);
    }
  }, [isPanelOpen, trackEvent, pageName]);

  const handleSearch = useCallback((event: any) => {
    setSearchText(event?.target?.value || '');
  }, []);

  const sortedSourceRefreshDetails = useMemo(() => {
    const details = sourceRefreshDetails ?? [];
    const sortedDetails = details.map((detail) => ({
      ...detail,
      pccDetails: detail.pccDetails ? [...detail.pccDetails].sort((a, b) => a.pccName.localeCompare(b.pccName)) : detail.pccDetails,
    }));
    if (sortedDetails.some(({ sourceName }) => sourceName === PRODUCT_FINDER_SOURCE_NAME)) {
      return sortedDetails;
    }

    const productFinderDetail: TDataRefreshDetail = {
      fiscalYear: selectedParticipation?.fiscalYearId ?? 0,
      sourceName: PRODUCT_FINDER_SOURCE_NAME,
      pccDetails: [],
    };
    return [...sortedDetails, productFinderDetail];
  }, [selectedParticipation?.fiscalYearId, sourceRefreshDetails]);

  const filteredSourceRefreshDetails = useMemo(() => {
    const normalizedSearchText = searchText.trim().toLowerCase();
    if (!normalizedSearchText) {
      return sortedSourceRefreshDetails;
    }

    return sortedSourceRefreshDetails.filter((detail) => {
      const searchableFields = [
        detail.sourceName,
        detail.nextSourceRefreshFrequency,
        detail.lastSourceRefreshDateTime,
        detail.lastReportRefreshDateTime,
        ...(detail.pccDetails?.map((pcc) => pcc.pccName) || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchableFields.includes(normalizedSearchText);
    });
  }, [sortedSourceRefreshDetails, searchText]);

  const hasDetails = !isError && filteredSourceRefreshDetails.length > 0;

  return (
    <FlyInPanel
      heading={t('dataRefreshPanelTitle')}
      style={{ ...largePanelStyle, zIndex: 3 }}
      open={isPanelOpen}
      onHeFlyInAfterHide={handlePanelClosing}
      slot="panel"
      contained
    >
      <div style={infoContainerStyles}>
        <Text appearance="body-text">
          <strong>{`${t('dataRefreshUnderstandingTitle')} `}</strong>
          {t('dataRefreshUnderstandingBody')}
        </Text>
      </div>

      <div style={searchContainerStyles}>
        <SearchBox
          onHeInput={handleSearch}
          onHeClear={handleSearch}
          style={searchBoxStyle}
          placeholder={t('placeHolderSearchNotification')}
          label={t('placeHolderSearchNotification')}
          hideLabel
        />
      </div>

      {isFetching && (
        <div style={loadingContainerStyles} data-testid="data-refresh-loading">
          <ProgressRing indeterminate label={t('dataRefreshLoading')} />
        </div>
      )}

      {!isFetching && isError && (
        <MessageBar appearance="error" expanded open>
          {t('dataRefreshError')}
        </MessageBar>
      )}

      {!isFetching && !isError && (
        <div style={contentContainerStyles}>
          <div style={headerRowStyles}>
            <Text appearance="label" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
              {t('dataRefreshColumnDataSource')}
            </Text>
            <Text appearance="label" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
              {t('dataRefreshColumnMetricsImpacted')}
            </Text>
            <Text appearance="label" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
              {t('dataRefreshColumnSourceRefreshTime')}
            </Text>
            <Text appearance="label" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
              {t('dataRefreshColumnReportRefreshTime')}
            </Text>
            <Text appearance="label" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
              {t('dataRefreshColumnRefreshFrequency')}
            </Text>
          </div>
          {hasDetails ? (
            filteredSourceRefreshDetails.map((detail) => <DataRefreshSourceRow key={`${detail.sourceName}-${detail.fiscalYear}`} detail={detail} />)
          ) : (
            <div style={loadingContainerStyles}>
              <Text appearance="body-text">{t('dataRefreshNoData')}</Text>
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
        <MessageBar appearance="notice" open>
          {t('dataRefreshFooterNote')}
        </MessageBar>
      </div>
    </FlyInPanel>
  );
};

export default DataRefreshPanel;
